import React, { useEffect, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { GlobalContext } from 'store/context/GlobalContext';
import MenuIcon from 'layout/components/Header/MenuIcon';
import LogoImage from 'components/LogoImage';
import Icons from 'components/Icons';
import UnitBox from './UnitBox';
import useWindowSize from 'components/useWindowSize';
import * as action from "store/actions";
import useGa from 'hooks/useGa';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

const Nav = () => {
    const gaTracking = useGa();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.Auth.isAuthenticated);
    const resize = useWindowSize();
    const location = useLocation();
    const context = useContext(GlobalContext);
    const { setLoader, menuToggle, setMenuToggle } = context;
    const menuRef = useRef(null);

    const NavMenu = [
        {
            label: "輸入序號",
            link: "/exchange",
            activeLink: "/exchange",
            auth: true,
            ga: "menu-exchange",
        },
        {
            label: "我的序號",
            activeLink: "/serial-number",
            auth: true,
            ga: "menu-serial-number",
            children: [
                {
                    label: "登錄序號明細",
                    ga: "menu-exchange-record",
                    link: '/serial-number#exchange',
                },
                {
                    label: "獎品兌換紀錄",
                    ga: "menu-giveaway-record",
                    link: '/serial-number#giveaway',
                },
                {
                    label: "選邊戰投票紀錄",
                    ga: "menu-vote-record",
                    link: '/serial-number#vote',
                }
            ],
        },
        {
            label: "烤宴選物集點換",
            link: "/giveaway",
            activeLink: "/giveaway",
            auth: true,
            ga: "menu-giveaway",
        },
        {
            label: "選邊戰得獎名單",
            link: "/award",
            activeLink: "/award",
            auth: false,
            ga: "menu-award",
        },
        {
            label: "活動辦法",
            link: "/activity",
            activeLink: "/activity",
            auth: false,
            ga: "menu-activity",
        },
        {
            label: "購買通路",
            link: "/where-to-buy",
            activeLink: "/where-to-buy",
            auth: false,
            ga: "menu-where-to-buy",
        },
    ]

    useEffect(() => {
        if(resize.width && resize.width >= 1200){
            setMenuToggle(false);
        }
    }, [resize.width])

    useEffect(() => {
        menuToggle ? disablePageScroll() : enablePageScroll();
    }, [menuToggle])

    useEffect(() => {
        return () => enablePageScroll();
    }, [])

    useEffect(() => {
        setMenuToggle(false);
    }, [location])

    const handleToggle = (e) => {
        e.stopPropagation();
        if(!menuRef.current.contains(e.target)) {
            setMenuToggle(false);
        }
    }

    const handleLogout = () => {
        setLoader(true);
        setTimeout(() => {
            dispatch(action.authLogout());
            gaTracking("logout");
            setLoader(false);
        }, 1000)
    }

    return (<>
        <nav className={`nav-bar${menuToggle ? ' open' : ''}`}>
            <Link className="logo-container" to="/" onClick={() => gaTracking("logo")}>
                <LogoImage/>
            </Link>
            <div className="nav-container" onClick={handleToggle}>
                <div className="nav-menu" ref={menuRef}>
                    <div className="menu-head">
                        <i className="menu-close" onClick={() => {
                            gaTracking("menu-close");
                            setMenuToggle(false);
                        }}></i>
                    </div>
                    <div className="menu-content" data-scroll-lock-scrollable>
                        <div className="menu-box">
                            <ul className="nav-list">
                                {
                                    NavMenu.map((item, index) =>
                                        ((item.auth && isAuthenticated) || !item.auth) &&
                                        <li className={`nav-item${location.pathname.startsWith(item.activeLink) ? ` active` : ''}`} key={index}>
                                            { item.link ? <Link className="nav-label" to={item.link} onClick={() => gaTracking(item.ga)}>{item.label}</Link> : <UnitBox item={item} /> }
                                        </li>
                                    )
                                }
                                {
                                    isAuthenticated &&
                                    <li className="nav-item logout">
                                        <div className="nav-label" onClick={handleLogout}>登出</div>
                                    </li>
                                }
                            </ul>
                            <div className="social-menu">
                                <a href="https://www.facebook.com/tw16asahi" target="_blank" className="text-center mgx-2 d-flex justify-content-center align-items-center flex-column" onClick={() => gaTracking("menu-fb-16cha")}>
                                    <Icons icon="fb" style={{width: "30px", height: "30px"}} />
                                    <div className="font-size-14 fw-bold mgt-1">十六茶</div>
                                </a>
                                <a href="https://www.facebook.com/calpis.tw" target="_blank" className="text-center mgx-2 d-flex justify-content-center align-items-center flex-column" onClick={() => gaTracking("menu-fb-calpis")}>
                                    <Icons icon="fb" style={{width: "30px", height: "30px"}} />
                                    <div className="font-size-14 fw-bold mgt-1">「可爾必思」</div>
                                </a>
                                <a href="https://www.asahisoftdrinks.com.tw/" target="_blank" className="text-center mgx-2 d-flex justify-content-center align-items-center flex-column" onClick={() => gaTracking("menu-offical-website")}>
                                    <Icons icon="calpis" style={{width: "30px", height: "30px"}} />
                                    <div className="font-size-14 fw-bold mgt-1">官網</div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="d-flex align-items-center">
                <a href="https://www.facebook.com/tw16asahi" target="_blank" className="text-center mgl-2 justify-content-center align-items-center flex-column mobile-hidden" onClick={() => gaTracking("menu-fb-16cha")}>
                    <Icons icon="fb" style={{width: "26px", height: "26px"}} />
                    <div className="font-size-12 fw-bold" style={{marginTop: "3px"}}>十六茶</div>
                </a>
                <a href="https://www.facebook.com/calpis.tw" target="_blank" className="text-center mgl-2 justify-content-center align-items-center flex-column mobile-hidden" onClick={() => gaTracking("menu-fb-calpis")}>
                    <Icons icon="fb" style={{width: "26px", height: "26px"}} />
                    <div className="font-size-12 fw-bold" style={{marginTop: "3px"}}>「可爾必思」</div>
                </a>
                <a href="https://www.asahisoftdrinks.com.tw/" target="_blank" className="text-center mgl-2 justify-content-center align-items-center flex-column mobile-hidden" onClick={() => gaTracking("menu-offical-website")}>
                    <Icons icon="calpis" style={{width: "26px", height: "26px"}} />
                    <div className="font-size-12 fw-bold" style={{marginTop: "3px"}}>官網</div>
                </a>
                {
                    isAuthenticated && 
                    <button className="logout-btn mobile-hidden" onClick={handleLogout}>登出</button>
                }
                <MenuIcon sx={{marginLeft: "18px"}} />
            </div>
        </nav>
    </>)
}

export default Nav;